// Implements: F-UIX-018, F-OBS-011
/**
 * The wire shapes of the org admin's audit list (OBS.8, design 5.4), as
 * `backend/.../observability/infrastructure/http/router_audit.py` answers them:
 *
 * ```
 * GET /organizations/{org_id}/audit?from=&to=&action=&team_id=&mockup_id=&cursor=
 *     200 {organization_id, from, to, items: [AuditRowView], next_cursor}
 * ```
 *
 * A row names its actor by the current full name, `"Persona eliminada"` with `actor_deleted: true`
 * for a person who no longer exists, or `null` for an action that names nobody (a visitor's wrong
 * share password, a revocation done by the system). It never carries a person id, an IP or any
 * content. Pages are a fixed 50 rows, newest first; `next_cursor` is `null` on the last one.
 *
 * Since S13a-1 a row names its mockup as the reader may see it now (`mockup_name`), trashed and
 * never-generated mockups included, `mockup_trashed` marking one in the trash; `mockup_name` is
 * `null` when the row names no mockup or there is no name to show (deleted, moved out, unreadable).
 * A `cursor` reused with other filters or another organization is a `422` `VALIDATION`.
 */

/** The closed vocabulary of audited actions (`AuditAction` on the server), in its order. */
export const AUDIT_ACTIONS = [
  "mockup_created",
  "mockup_scope_changed",
  "mockup_trashed",
  "mockup_restored",
  "mockup_deleted",
  "version_generated",
  "share_link_created",
  "share_link_expiry_changed",
  "share_link_revoked",
  "share_link_revoked_by_system",
  "share_password_failed",
  "login_failed",
  "login_blocked",
  "usage_ceiling_changed",
  "usage_distribution_changed",
  "sender_linked",
  "sender_unlinked",
  // Mockup v3 P5: a design profile renamed (`AuditAction.DESIGN_PROFILE_RENAMED`).
  "design_profile_renamed",
] as const;

export type AuditAction = (typeof AUDIT_ACTIONS)[number];

export interface AuditRowView {
  /** An instant in UTC with a `Z`. */
  readonly occurred_at: string;
  /** A token of `AUDIT_ACTIONS`; typed `string` because a newer server may add one. */
  readonly action: string;
  readonly actor: string | null;
  readonly actor_deleted: boolean;
  readonly target_id: string | null;
  readonly mockup_id: string | null;
  readonly team_id: string | null;
  /** The mockup's current name for the reader, or `null` (no mockup, or no name to show). */
  readonly mockup_name: string | null;
  /** Whether that named mockup is in the trash; `false` when it is not named. */
  readonly mockup_trashed: boolean;
}

export interface AuditPageView {
  readonly organization_id: string;
  /** The first and last calendar days covered, inclusive, defaults resolved. */
  readonly from: string;
  readonly to: string;
  readonly items: readonly AuditRowView[];
  readonly next_cursor: string | null;
}

/** Every action, one action, or both revocations (by a person and by the system). */
export type ActionFilter = "" | AuditAction | "all_revocations";

/** What the org admin filters the list by; an empty value is no filter. */
export interface AuditFilters {
  /** `YYYY-MM-DD` or empty (the server's default). */
  readonly from: string;
  readonly to: string;
  readonly action: ActionFilter;
  /** A team id or empty (every team). */
  readonly teamId: string;
  /** A mockup id or empty (every mockup). */
  readonly mockupId: string;
}

/** An organization whose trail the server lets the person read. */
export interface AuditTarget {
  readonly organizationId: string;
  readonly name: string | null;
  /** Whether the person may also read the organization's per-mockup list, which names mockups. */
  readonly namesMockups: boolean;
}
